import { FC, useMemo } from 'react';

import { startOfDay } from 'date-fns';
import { getDateFromString, getMaxDate, getMinDate } from 'utils/date';

import auctionCampaignPromotionDatesButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_promotion_dates_button_click';

import useTrls from 'hooks/translation/useTrls';

import { useAuctionDateValidation } from 'components/AuctionBuilder/AuctionPeriod/useAuctionDateValidation';
import { DateSelect } from 'components/common/DateSelect/DateSelect';

import {
    AuctionBuilderDatesErrorCode,
    AuctionDataField,
    auctionSelector,
    setIsDatesChanged,
    setStartDate,
} from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    datePlaceholder: 'AuctionBuilder.CampaignPeriod.AuctionDateStart.placeholder',
    error: {
        [AuctionBuilderDatesErrorCode.ValueMissingForStrategy]:
            'AuctionBuilder.CampaignPeriod.error.valueMissingForStrategy',
    },
};

const TODAY = startOfDay(new Date());

export const AuctionDateStart: FC = () => {
    const startDate = useSelector(auctionSelector.startDate);
    const endDate = useSelector(auctionSelector.endDate);
    const campaignStartDate = useSelector(auctionSelector.auctionCampaign)?.startAt;
    const campaignExpirationDate = useSelector((state) => state.user.user?.currentAccount.expirationDate);
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const campaignId = useSelector(auctionSelector.auctionCampaign)?.campaignId;
    const errorCode = useSelector(auctionSelector.errors.startAt);

    const dispatch = useDispatch();
    const trls = useTrls();
    const validateDate = useAuctionDateValidation();

    const convertedCampaignStartDate = getDateFromString(campaignStartDate);
    const selectedDate = getDateFromString(startDate);

    const isInputDisabled = convertedCampaignStartDate ? convertedCampaignStartDate < TODAY : false;

    const minDate = useMemo(() => {
        const dates = [convertedCampaignStartDate, TODAY].filter((date) => !!date) as Date[];
        return getMinDate(dates);
    }, [convertedCampaignStartDate]);

    const maxDate = useMemo(() => {
        const dates = [getDateFromString(endDate), getDateFromString(campaignExpirationDate)].filter(
            (date) => !!date
        ) as Date[];
        return getMaxDate(dates);
    }, [campaignExpirationDate, endDate]);

    const onDateSelect = (date: string) => {
        dispatch([setStartDate(date), setIsDatesChanged(true)]);

        validateDate(date, AuctionDataField.StartAt);

        auctionCampaignPromotionDatesButtonClick({
            vacancyId,
            campaignId,
            startAt: date,
            endAt: endDate,
        });
    };

    return (
        <DateSelect
            data-qa="auction-date-start-input"
            maxDate={maxDate}
            minDate={minDate}
            onDateSelect={onDateSelect}
            placeholder={trls(TrlKeys.datePlaceholder)}
            selectedDate={selectedDate || undefined}
            inputDisabled={isInputDisabled}
            inputInvalid={!!errorCode}
            errorMessage={
                errorCode ? trls(TrlKeys.error[AuctionBuilderDatesErrorCode.ValueMissingForStrategy]) : undefined
            }
        />
    );
};
